<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatTime } from '@chatwoot/utils';
import { useStore } from 'dashboard/composables/store';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';

const { t } = useI18n();
const store = useStore();

const since = ref(0);
const until = ref(0);
const businessHours = ref(false);
const searchAgent = ref('');
const selectedBrandId = ref('');
const sortBy = ref('sessions_desc');
const hideInactive = ref(false);

const agents = computed(
  () => store.getters['crossBrandReports/getAgents'] || []
);
const accounts = computed(
  () => store.getters['crossBrandReports/getAccounts'] || []
);
const meta = computed(() => store.getters['crossBrandReports/getMeta'] || {});
const uiFlags = computed(
  () => store.getters['crossBrandReports/getUIFlags'] || {}
);
const isLoading = computed(() => uiFlags.value?.isFetchingAgents);

const fmtTime = v => (!v || v === 0 ? '—' : formatTime(v));
const fmtNum = v => Number(v || 0).toLocaleString();

const tableData = computed(() => {
  let data = agents.value.map(agent => ({
    id: agent.id,
    name: agent.name,
    email: agent.email,
    avatar: agent.avatarUrl,
    sessions: agent.sessionsCount || 0,
    firstResponse: agent.avgFirstResponseTime || 0,
    resolution: agent.avgResolutionTime || 0,
    replyTime: agent.avgReplyTime || 0,
    accountsCount: agent.accountsCount || 0,
    accounts: agent.accounts || [],
  }));

  if (selectedBrandId.value) {
    data = data.filter(a =>
      a.accounts.some(
        acc => String(acc.accountId) === String(selectedBrandId.value)
      )
    );
  }

  if (searchAgent.value) {
    const s = searchAgent.value.toLowerCase();
    data = data.filter(
      a =>
        a.name?.toLowerCase().includes(s) || a.email?.toLowerCase().includes(s)
    );
  }

  if (hideInactive.value) {
    data = data.filter(a => a.sessions > 0);
  }

  const [field, dir] = sortBy.value.split('_');
  const dirSign = dir === 'asc' ? 1 : -1;
  data.sort((a, b) => {
    let va;
    let vb;
    if (field === 'sessions') {
      va = a.sessions;
      vb = b.sessions;
    } else if (field === 'first') {
      va = a.firstResponse;
      vb = b.firstResponse;
    } else if (field === 'resolution') {
      va = a.resolution;
      vb = b.resolution;
    } else if (field === 'name') {
      va = a.name?.toLowerCase();
      vb = b.name?.toLowerCase();
    } else {
      va = a.sessions;
      vb = b.sessions;
    }
    if (typeof va === 'string') return va.localeCompare(vb) * dirSign;
    return (va - vb) * dirSign;
  });

  return data;
});

const totalSessions = computed(() =>
  tableData.value.reduce((s, a) => s + a.sessions, 0)
);

const fetchData = () => {
  if (!since.value || !until.value) return;
  store.dispatch('crossBrandReports/fetchAgents', {
    since: since.value,
    until: until.value,
    businessHours: businessHours.value,
  });
};

const onFilterChange = ({ from, to }) => {
  since.value = from;
  until.value = to;
  fetchData();
};

const exportToCSV = () => {
  const headers = [
    'Agent',
    'Email',
    'Brands',
    'Sessions',
    'First Response (sec)',
    'Resolution (sec)',
    'Reply Time (sec)',
  ];
  const rows = tableData.value.map(a => [
    a.name,
    a.email,
    a.accounts.map(acc => acc.accountName).join('; '),
    a.sessions,
    Math.round(a.firstResponse || 0),
    Math.round(a.resolution || 0),
    Math.round(a.replyTime || 0),
  ]);
  const csv = [
    headers.join(','),
    ...rows.map(r => r.map(c => `"${c}"`).join(',')),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `all_brands_${new Date().toISOString().split('T')[0]}.csv`;
  a.style.visibility = 'hidden';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

onMounted(fetchData);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="ab-page">
    <div class="ab-shell">
      <div class="ab-header">
        <div class="ab-header-left">
          <button
            class="ab-side-toggle"
            type="button"
            aria-label="Toggle side navigation"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="16"
              height="16"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 4v16M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z"
              />
            </svg>
          </button>
          <h1 class="ab-title">{{ $t('CROSS_BRAND_REPORTS.HEADER') }}</h1>
        </div>
        <button class="ab-export-btn" type="button" @click="exportToCSV">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            width="16"
            height="16"
          >
            <path
              stroke="currentcolor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2m-8-1V4m0 12 5-5m-5 5-5-5"
            />
          </svg>
          <span>Export CSV</span>
        </button>
      </div>

      <div class="ab-toolbar">
        <div class="ab-toolbar-left">
          <CompactDateInboxFilters
            :show-inbox="false"
            @filter-change="onFilterChange"
          />
        </div>
      </div>

      <div class="ab-filters-bar">
        <div class="ab-filter-field">
          <label class="ab-filter-label">Brand</label>
          <select v-model="selectedBrandId" class="ab-filter-select">
            <option value="">All brands</option>
            <option v-for="acc in accounts" :key="acc.id" :value="acc.id">
              {{ acc.name }}
            </option>
          </select>
        </div>

        <div class="ab-filter-field">
          <label class="ab-filter-label">Search agent</label>
          <input
            v-model="searchAgent"
            type="text"
            placeholder="Name or email…"
            class="ab-filter-input"
          />
        </div>

        <div class="ab-filter-field">
          <label class="ab-filter-label">Sort by</label>
          <select v-model="sortBy" class="ab-filter-select">
            <option value="sessions_desc">Sessions ↓</option>
            <option value="sessions_asc">Sessions ↑</option>
            <option value="first_asc">First response ↑</option>
            <option value="first_desc">First response ↓</option>
            <option value="resolution_asc">Resolution ↑</option>
            <option value="resolution_desc">Resolution ↓</option>
            <option value="name_asc">Name A→Z</option>
          </select>
        </div>

        <label class="ab-switch">
          <span
            class="ab-switch-track"
            :class="{ 'ab-switch-track--on': businessHours }"
          >
            <input
              v-model="businessHours"
              type="checkbox"
              class="ab-switch-input"
              @change="fetchData"
            />
            <span class="ab-switch-thumb" />
          </span>
          <span>Business hours</span>
        </label>

        <label class="ab-switch">
          <span
            class="ab-switch-track"
            :class="{ 'ab-switch-track--on': hideInactive }"
          >
            <input
              v-model="hideInactive"
              type="checkbox"
              class="ab-switch-input"
            />
            <span class="ab-switch-thumb" />
          </span>
          <span>Hide inactive</span>
        </label>
      </div>

      <div class="ab-content">
        <div class="ab-kpis">
          <div class="ab-kpi">
            <div class="ab-kpi-label">
              {{ $t('CROSS_BRAND_REPORTS.STATS.TOTAL_AGENTS') }}
            </div>
            <div class="ab-kpi-value">{{ tableData.length }}</div>
          </div>
          <div class="ab-kpi">
            <div class="ab-kpi-label">
              {{ $t('CROSS_BRAND_REPORTS.STATS.TOTAL_BRANDS') }}
            </div>
            <div class="ab-kpi-value">
              {{ meta.totalAccounts || accounts.length }}
            </div>
          </div>
          <div class="ab-kpi">
            <div class="ab-kpi-label">
              {{ $t('CROSS_BRAND_REPORTS.STATS.TOTAL_SESSIONS') }}
            </div>
            <div class="ab-kpi-value">{{ fmtNum(totalSessions) }}</div>
          </div>
          <div class="ab-kpi">
            <div class="ab-kpi-label">
              {{ $t('CROSS_BRAND_REPORTS.STATS.FILTERED') }}
            </div>
            <div class="ab-kpi-value">
              {{ tableData.length }} / {{ agents.length }}
            </div>
          </div>
        </div>

        <section class="ab-card">
          <div class="ab-card-header">
            <div class="ab-card-title">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"
                />
              </svg>
              <span>{{ $t('CROSS_BRAND_REPORTS.RESULTS_TITLE') }}</span>
              <span class="ab-card-count">{{ tableData.length }}</span>
            </div>
          </div>

          <div class="ab-table-scroll">
            <div v-if="isLoading" class="ab-loading">
              <div class="ab-spinner" />
            </div>
            <div v-else-if="!tableData.length" class="ab-empty">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                width="40"
                height="40"
                class="ab-empty-icon"
              >
                <path
                  stroke="#d1d5db"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  d="M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z"
                />
              </svg>
              {{
                agents.length
                  ? 'No agents match your filters.'
                  : $t('CROSS_BRAND_REPORTS.NO_DATA')
              }}
            </div>
            <table v-else class="ab-tbl">
              <thead>
                <tr>
                  <th class="ab-th ab-th--agent ab-sticky">
                    {{ $t('CROSS_BRAND_REPORTS.TABLE.AGENT') }}
                  </th>
                  <th class="ab-th">
                    {{ $t('CROSS_BRAND_REPORTS.TABLE.BRANDS') }}
                  </th>
                  <th class="ab-th">
                    {{ $t('CROSS_BRAND_REPORTS.TABLE.SESSIONS') }}
                  </th>
                  <th class="ab-th">
                    {{ $t('CROSS_BRAND_REPORTS.TABLE.FIRST_RESPONSE') }}
                  </th>
                  <th class="ab-th">
                    {{ $t('CROSS_BRAND_REPORTS.TABLE.RESOLUTION') }}
                  </th>
                  <th class="ab-th">
                    {{ $t('CROSS_BRAND_REPORTS.TABLE.REPLY_TIME') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in tableData" :key="row.id" class="ab-row">
                  <td class="ab-td ab-td--agent ab-sticky">
                    <div class="ab-agent">
                      <div class="ab-avatar">
                        <img
                          v-if="row.avatar"
                          :src="row.avatar"
                          :alt="row.name"
                          class="ab-avatar-img"
                          loading="lazy"
                        />
                        <span v-else class="ab-avatar-ini">{{
                          (row.name || '?')[0]
                        }}</span>
                      </div>
                      <div class="ab-agent-info">
                        <span class="ab-agent-name">{{ row.name }}</span>
                        <span class="ab-agent-email">{{ row.email }}</span>
                      </div>
                    </div>
                  </td>
                  <td class="ab-td">
                    <div class="ab-brands-count">{{ row.accountsCount }}</div>
                    <div class="ab-brands-names">
                      {{ row.accounts.map(a => a.accountName).join(', ') }}
                    </div>
                  </td>
                  <td class="ab-td ab-td--num">{{ fmtNum(row.sessions) }}</td>
                  <td
                    class="ab-td ab-td--num"
                    :class="row.firstResponse > 120 ? 'ab-td--warn' : ''"
                  >
                    {{ fmtTime(row.firstResponse) }}
                  </td>
                  <td
                    class="ab-td ab-td--num"
                    :class="row.resolution > 3600 ? 'ab-td--warn' : ''"
                  >
                    {{ fmtTime(row.resolution) }}
                  </td>
                  <td class="ab-td ab-td--num">{{ fmtTime(row.replyTime) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>
<!-- eslint-enable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->

<style scoped>
.ab-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #111827;
}

.ab-shell {
  min-width: 650px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
}

.ab-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
}

.ab-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ab-side-toggle {
  width: 40px;
  height: 40px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #111827;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.ab-side-toggle:hover {
  background: #f3f4f6;
}

.ab-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}

.ab-export-btn {
  height: 36px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.ab-export-btn:hover {
  background: #f9fafb;
}

.ab-toolbar {
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0 3px 10px -6px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.ab-toolbar-left {
  flex: 1;
}

/* ── Filters bar ── */
.ab-filters-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 10px 24px;
  border-bottom: 1px solid #f3f4f6;
  background: #fafafa;
}

.ab-filter-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ab-filter-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.ab-filter-select,
.ab-filter-input {
  height: 34px;
  border-radius: 8px;
  border: 1px solid #d9dde3;
  background: #f3f4f6;
  color: #111827;
  font-size: 13px;
  font-weight: 600;
  padding: 0 10px;
}

.ab-filter-select {
  min-width: 180px;
  cursor: pointer;
}
.ab-filter-input {
  min-width: 200px;
}
.ab-filter-input:focus {
  outline: none;
  border-color: #0076f5;
}

.ab-switch {
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  user-select: none;
  cursor: pointer;
}

.ab-switch-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.ab-switch-track {
  position: relative;
  width: 27px;
  height: 20px;
  border-radius: 15px;
  background: rgb(221, 221, 221);
  cursor: pointer;
  transition: background 0.2s ease;
}

.ab-switch-track--on {
  background: #111827;
}

.ab-switch-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.1) 0 2px 2px 0;
  transition: left 0.2s ease-in-out;
}

.ab-switch-track--on .ab-switch-thumb {
  left: 10px;
}

/* ── Content ── */
.ab-content {
  padding: 16px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ── KPI cards ── */
.ab-kpis {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.ab-kpi {
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid #ebedf0;
  background: #f3f4f6;
}

.ab-kpi-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.ab-kpi-value {
  margin-top: 8px;
  font-size: 28px;
  line-height: 1.05;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.03em;
}

/* ── Table card ── */
.ab-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #ffffff;
  overflow: hidden;
}

.ab-card-header {
  min-height: 56px;
  padding: 0 16px;
  border-bottom: 1px solid #dfe3e8;
  display: flex;
  align-items: center;
}

.ab-card-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.ab-card-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: #e5e7eb;
  color: #374151;
  font-size: 12px;
  font-weight: 700;
}

/* ── Loading / empty ── */
.ab-loading,
.ab-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 16px;
  color: #6b7280;
  font-size: 13px;
}

.ab-empty-icon {
  opacity: 0.5;
}

.ab-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: ab-spin 0.8s linear infinite;
}

@keyframes ab-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Table ── */
.ab-table-scroll {
  overflow-x: auto;
  scrollbar-width: thin;
  scrollbar-color: #cfd4dc transparent;
}

.ab-tbl {
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
  background: #ffffff;
}

.ab-th {
  padding: 10px 16px;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 600;
  color: #111827;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
  text-align: center;
  text-transform: none;
}

.ab-th--agent {
  text-align: left;
  min-width: 240px;
}

.ab-sticky {
  position: sticky;
  left: 0;
  z-index: 5;
  background: #ffffff;
  box-shadow: 8px 0 12px -12px rgba(0, 0, 0, 0.25);
}

.ab-th.ab-sticky {
  z-index: 6;
  background: #f8fafc;
}

.ab-td {
  padding: 10px 16px;
  border-bottom: 1px solid #f3f4f6;
  background: #ffffff;
  vertical-align: middle;
  font-size: 13px;
  color: #374151;
}

.ab-td--agent {
  min-width: 240px;
}
.ab-td--num {
  text-align: center;
  font-weight: 600;
  color: #111827;
}
.ab-td--warn {
  color: #dc2626;
}

.ab-row:hover .ab-td {
  background: #fafafa;
}
.ab-row:hover .ab-sticky {
  background: #fafafa;
}

.ab-agent {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ab-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  overflow: hidden;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ab-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.ab-avatar-ini {
  font-size: 14px;
  font-weight: 700;
  color: #374151;
}

.ab-agent-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.ab-agent-name {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ab-agent-email {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ab-brands-count {
  font-size: 13px;
  font-weight: 700;
  color: #111827;
}
.ab-brands-names {
  font-size: 11px;
  color: #6b7280;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

::global(body.dark) .ab-page,
::global(body.dark) .ab-shell,
::global(body.dark) .ab-header,
::global(body.dark) .ab-toolbar,
::global(body.dark) .ab-filters-bar,
::global(body.dark) .ab-card,
::global(body.theme-mix) .ab-page,
::global(body.theme-mix) .ab-shell,
::global(body.theme-mix) .ab-header,
::global(body.theme-mix) .ab-toolbar,
::global(body.theme-mix) .ab-filters-bar,
::global(body.theme-mix) .ab-card {
  background: #ffffff !important;
  color: #111827 !important;
}

@media (max-width: 1200px) {
  .ab-kpis {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 1100px) {
  .ab-content {
    padding: 12px 16px 16px;
  }
  .ab-toolbar {
    padding: 10px 16px 8px;
  }
  .ab-filters-bar {
    padding: 8px 16px;
  }
}
</style>
